/**
 * LLM Configs (ADR-0033 Phase A) — the operator manages N named LLM
 * configurations (provider / model / credential / endpoint + optional monthly
 * RPM cap + a default flag). The monthly BUDGET is not here: it's the tenant total
 * in Impostazioni (the bill arrives per API key, and configs can share one).
 * Agents bind a config (Phase B);
 * `build_model` resolves a specific one or the default. Under the `ai` macro-area.
 *
 * Native `<select>` is intentional: this is the operator console.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { usePermissions } from "@/lib/permissions";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  type LlmConfig,
  type LlmConfigInput,
  type LlmProviderInfo,
  llmConfigsApi,
} from "@/lib/api-ai";

const inputCls = "w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm";

const KEY = ["ai", "llm-configs"] as const;

export function LlmConfigsPage() {
  const { can } = usePermissions();
  const canWrite = can("ai", "write");
  const qc = useQueryClient();
  const q = useQuery({ queryKey: KEY, queryFn: () => llmConfigsApi.list() });
  const [editing, setEditing] = useState<LlmConfig | "new" | null>(null);

  const invalidate = () => void qc.invalidateQueries({ queryKey: KEY });
  const setDefault = useMutation({
    mutationFn: (id: string) => llmConfigsApi.setDefault(id),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => llmConfigsApi.remove(id),
    onSuccess: invalidate,
  });

  const configs = q.data?.configs ?? [];
  const providers = q.data?.providers ?? [];

  return (
    <div className="mx-auto max-w-5xl p-6">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold">Configurazioni LLM</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Registra qui le credenziali del provider LLM (Anthropic, OpenAI, Ollama locale, …). Gli
            agenti scelgono una di queste configurazioni; una è la <strong>predefinita</strong>. La
            chiave resta cifrata nel vault e non viene mai mostrata.
          </p>
        </div>
        {canWrite && (
          <button
            type="button"
            onClick={() => setEditing("new")}
            className="shrink-0 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-accent-foreground"
          >
            + Nuova
          </button>
        )}
      </div>

      {q.isLoading && <p className="text-sm text-muted-foreground">Caricamento…</p>}
      {configs.length === 0 && !q.isLoading && (
        <p className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          Nessuna configurazione. {canWrite && 'Crea la prima con "+ Nuova".'}
        </p>
      )}

      {configs.length > 0 && (
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="py-2">Nome</th>
              <th className="py-2">Provider / Modello</th>
              <th className="py-2">Speso (mese)</th>
              <th className="py-2">Stato</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {configs.map((c) => (
              <tr key={c.id} className="border-b border-border/60">
                <td className="py-2 font-medium">
                  {c.name}
                  {c.is_default && (
                    <span className="ml-2 rounded bg-accent/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-accent">
                      default
                    </span>
                  )}
                </td>
                <td className="py-2">
                  <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
                    {c.provider}
                  </span>{" "}
                  <span className="text-muted-foreground">{c.model}</span>
                </td>
                <td className="py-2 text-xs">{spendLabel(c)}</td>
                <td className="py-2 text-xs">
                  {!c.enabled ? (
                    <span className="text-muted-foreground">disabilitato</span>
                  ) : c.configured ? (
                    <span className="text-success">attivo</span>
                  ) : (
                    <span className="text-warning">incompleto</span>
                  )}
                </td>
                <td className="py-2 text-right">
                  {canWrite && (
                    <div className="flex justify-end gap-2 text-xs">
                      {!c.is_default && (
                        <button
                          type="button"
                          className="text-accent"
                          onClick={() => setDefault.mutate(c.id)}
                        >
                          Default
                        </button>
                      )}
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-foreground"
                        onClick={() => setEditing(c)}
                      >
                        Modifica
                      </button>
                      <button
                        type="button"
                        className="text-danger"
                        onClick={() => {
                          if (window.confirm(`Eliminare «${c.name}»?`)) remove.mutate(c.id);
                        }}
                      >
                        Elimina
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <ConfigDialog
          providers={providers}
          existing={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            invalidate();
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

// The spend ATTRIBUTED to this config this month. Informational, NOT a cap: the
// budget is the tenant TOTAL (Impostazioni → Budget AI), because the bill arrives per
// API key and several configs can share one.
function spendLabel(c: LlmConfig): string {
  return `$${c.spend_this_month.toFixed(2)}`;
}

function ConfigDialog({
  providers,
  existing,
  onClose,
  onSaved,
}: {
  providers: LlmProviderInfo[];
  existing: LlmConfig | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(existing?.name ?? "");
  const [provider, setProvider] = useState(existing?.provider ?? providers[0]?.provider_id ?? "");
  const [model, setModel] = useState(existing?.model ?? "");
  const [apiKey, setApiKey] = useState("");
  const [baseUrl, setBaseUrl] = useState(existing?.base_url ?? "");
  const [rpm, setRpm] = useState(existing?.rate_limit_rpm?.toString() ?? "");
  const [isDefault, setIsDefault] = useState(existing?.is_default ?? false);
  // ADR-0034: the model's native-input modalities are DATA (declared here, not hardcoded in
  // code). Seed from the row's effective value; for a new/undeclared config, auto-prefill from
  // the per-(provider,model) SUGGESTION until the operator ticks a box (then it's their call).
  const [modalities, setModalities] = useState<string[]>(existing?.input_modalities ?? []);
  const [modTouched, setModTouched] = useState(Boolean(existing?.modalities_declared));
  // ADR-0035 RAG: a config is either a `chat` model or an `embedding` model (which a
  // Knowledge Base binds). Embedding configs hide the chat-only "visione" block.
  const [kind, setKind] = useState(existing?.kind ?? "chat");
  const [err, setErr] = useState<string | null>(null);

  const current = providers.find((p) => p.provider_id === provider);
  const needsKey = current?.requires_api_key ?? true;
  const needsUrl = current?.requires_base_url ?? false;

  // Pre-fill the modality checkboxes from the backend suggestion when the model changes and the
  // operator hasn't overridden them (a new model needs no deploy — the suggestion is just a default).
  useEffect(() => {
    if (modTouched || !provider || !model) return;
    let cancelled = false;
    void llmConfigsApi
      .modalitySuggestion(provider, model)
      .then((r) => {
        if (!cancelled) setModalities(r.caps);
      })
      .catch(() => {
        /* a suggestion failure just leaves the checkboxes as-is */
      });
    return () => {
      cancelled = true;
    };
  }, [provider, model, modTouched]);

  const toggleModality = (cap: string) => {
    setModTouched(true);
    setModalities((m) => (m.includes(cap) ? m.filter((c) => c !== cap) : [...m, cap]));
  };

  const save = useMutation({
    mutationFn: () => {
      const body: LlmConfigInput = {
        name,
        provider,
        model,
        kind,
        ...(apiKey ? { api_key: apiKey } : {}),
        ...(needsUrl ? { base_url: baseUrl } : {}),
        // Declared on save (the operator saw the checkboxes) — authoritative at runtime.
        // For an embedding config the chat modalities are irrelevant → send text-only.
        input_modalities: kind === "embedding" ? [] : modalities,
        rate_limit_rpm: rpm ? Number(rpm) : null,
        ...(existing ? {} : { is_default: isDefault }),
      };
      return existing ? llmConfigsApi.update(existing.id, body) : llmConfigsApi.create(body);
    },
    onSuccess: onSaved,
    onError: () => setErr("Salvataggio fallito (controlla provider, chiave o endpoint)."),
  });

  const valid = name && provider && model && (!needsUrl || baseUrl);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold">
            {existing ? `Modifica «${existing.name}»` : "Nuova configurazione LLM"}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="grid gap-3">
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-medium uppercase tracking-wide text-muted-foreground">Nome</span>
            <input
              className={inputCls}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="es. Anthropic principale"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-medium uppercase tracking-wide text-muted-foreground">Tipo</span>
            <select className={inputCls} value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="chat">Chat (modello assistente)</option>
              <option value="embedding">Embedding (per le basi di conoscenza)</option>
            </select>
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-xs">
              <span className="font-medium uppercase tracking-wide text-muted-foreground">
                Provider
              </span>
              <select
                className={inputCls}
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
              >
                {providers.map((p) => (
                  <option key={p.provider_id} value={p.provider_id}>
                    {p.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs">
              <span className="font-medium uppercase tracking-wide text-muted-foreground">
                Modello
              </span>
              <input
                className={inputCls}
                list="llm-models"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                placeholder="es. claude-sonnet-4-5"
              />
              <datalist id="llm-models">
                {(kind === "embedding"
                  ? (current?.default_embedding_models ?? [])
                  : (current?.default_models ?? [])
                ).map((m) => (
                  <option key={m} value={m} />
                ))}
              </datalist>
            </label>
          </div>
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-medium uppercase tracking-wide text-muted-foreground">
              API key{" "}
              {needsKey
                ? existing?.has_credential
                  ? "(lascia vuoto per non cambiarla)"
                  : ""
                : "(opzionale per endpoint locali)"}
            </span>
            <input
              className={inputCls}
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={
                existing?.has_credential ? "••••••••" : needsKey ? "sk-…" : "(opzionale)"
              }
            />
          </label>
          {needsUrl && (
            <label className="flex flex-col gap-1 text-xs">
              <span className="font-medium uppercase tracking-wide text-muted-foreground">
                Endpoint (URL)
              </span>
              <input
                className={inputCls}
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                placeholder={current?.base_url_hint || "http://localhost:11434/v1"}
              />
            </label>
          )}
          {kind !== "embedding" && (
            <div className="flex flex-col gap-1.5 text-xs">
              <span className="font-medium uppercase tracking-wide text-muted-foreground">
                Cosa vede il modello
              </span>
              <div className="flex flex-wrap gap-4">
                {[
                  { cap: "image", label: "Immagini" },
                  { cap: "pdf", label: "PDF" },
                ].map(({ cap, label }) => (
                  <label key={cap} className="flex items-center gap-1.5">
                    <input
                      type="checkbox"
                      checked={modalities.includes(cap)}
                      onChange={() => toggleModality(cap)}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
              <span className="text-[11px] text-muted-foreground">
                Immagini/PDF allegati in chat vengono passati nativamente al modello (se spuntati).
                Altri formati — o se non spuntato — vengono letti via codice. Precompilato in base al
                modello; modificabile.
              </span>
            </div>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-xs">
              <span className="font-medium uppercase tracking-wide text-muted-foreground">
                Rate limit RPM (opz.)
              </span>
              <input
                className={inputCls}
                type="number"
                min="1"
                value={rpm}
                onChange={(e) => setRpm(e.target.value)}
                placeholder="es. 60"
              />
            </label>
          </div>
          {!existing && (
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
              />
              <span>Imposta come configurazione default</span>
            </label>
          )}
          </div>
          {err && <p className="mt-3 text-xs text-danger">{err}</p>}
        </DialogBody>
        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-3 py-1.5 text-sm text-muted-foreground"
          >
            Annulla
          </button>
          <button
            type="button"
            disabled={!valid || save.isPending}
            onClick={() => {
              setErr(null);
              save.mutate();
            }}
            className="rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-accent-foreground disabled:opacity-50"
          >
            {save.isPending ? "Salvataggio…" : "Salva"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
